import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

const GENRES = [
  { key: "luxury_weddings", label: "Luxury Weddings" },
  { key: "corporate_background", label: "Corporate Background" },
  { key: "jazz_standards", label: "Jazz Standards" },
  { key: "pop_accompaniment", label: "Pop Accompaniment" },
  { key: "cultural_devotional", label: "Cultural / Devotional" },
  { key: "high_energy_party", label: "High-Energy Party" },
];

const COMFORT_LEVELS = [
  { value: "very_comfortable", label: "Very Comfortable" },
  { value: "comfortable", label: "Comfortable" },
  { value: "avoid", label: "Avoid" },
];

const SCENARIO_QUESTIONS = [
  {
    key: "unknown_song",
    question: "A client requests a song you don't know 10 minutes before performance. What do you do?",
  },
  {
    key: "volume_complaint",
    question: "Mid-set, the event planner tells you the client thinks you're too loud during dinner. How do you handle it?",
  },
  {
    key: "gear_failure",
    question: "Your instrument string breaks (or a key sticks, reed cracks, etc.) during a live ceremony. What's your move?",
  },
];

export interface GenreAssessmentData {
  genre_comfort: Record<string, string>;
  scenario_responses: Record<string, string>;
  /** @deprecated kept for backward compat */
  scenario_response?: string;
}

interface GenreAssessmentProps {
  onComplete: (data: GenreAssessmentData) => void;
}

export const GenreAssessment = ({ onComplete }: GenreAssessmentProps) => {
  const [genreComfort, setGenreComfort] = useState<Record<string, string>>({});
  const [scenarioResponses, setScenarioResponses] = useState<Record<string, string>>({});

  const allGenresAnswered = GENRES.every(g => genreComfort[g.key]);
  const allScenariosAnswered = SCENARIO_QUESTIONS.every(
    q => (scenarioResponses[q.key] || "").trim().length >= 20
  );
  const canSubmit = allGenresAnswered && allScenariosAnswered;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onComplete({
      genre_comfort: genreComfort,
      scenario_responses: scenarioResponses,
      scenario_response: scenarioResponses[SCENARIO_QUESTIONS[0].key],
    });
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <h3 className="text-xl font-bold">Genre & Luxury Context</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Rate your comfort level for each genre context
        </p>
      </div>

      {/* Genre matrix */}
      <div className="space-y-4">
        {GENRES.map(genre => (
          <div key={genre.key} className="p-3 rounded-lg border bg-card">
            <p className="text-sm font-medium mb-2">{genre.label}</p>
            <RadioGroup
              value={genreComfort[genre.key] || ""}
              onValueChange={(val) => setGenreComfort(prev => ({ ...prev, [genre.key]: val }))}
              className="flex gap-4"
            >
              {COMFORT_LEVELS.map(level => (
                <div key={level.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={level.value} id={`${genre.key}-${level.value}`} />
                  <Label htmlFor={`${genre.key}-${level.value}`} className="text-xs cursor-pointer">
                    {level.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        ))}
      </div>

      {/* Scenario questions */}
      <div className="space-y-5">
        <Label className="text-sm font-medium">
          Scenario Questions <span className="text-destructive">*</span>
        </Label>
        {SCENARIO_QUESTIONS.map((sq, i) => (
          <div key={sq.key} className="space-y-2 p-3 rounded-lg border bg-card">
            <p className="text-sm text-muted-foreground italic">
              {i + 1}. "{sq.question}"
            </p>
            <Textarea
              value={scenarioResponses[sq.key] || ""}
              onChange={(e) => setScenarioResponses(prev => ({ ...prev, [sq.key]: e.target.value }))}
              placeholder="Describe your approach..."
              className="min-h-[100px]"
              maxLength={1000}
            />
            <p className="text-xs text-muted-foreground text-right">
              {(scenarioResponses[sq.key] || "").length}/1000
            </p>
          </div>
        ))}
      </div>

      <Button onClick={handleSubmit} disabled={!canSubmit} className="w-full" size="lg">
        Submit Audition →
      </Button>
    </div>
  );
};